import type { DesktopPracticeBridge } from '@rudi/practice-protocol/bridge';
import type { DesktopVoiceBridge } from '../../../../desktop/src/voice/bridge';
/**
 * Runtime platform detection + Electron bridge.
 *
 * The Electron shell (`apps/desktop`) exposes `window.rudiDesktop` via its
 * preload script. A real browser never has this global. This is the single
 * source of truth for branching behavior between the two targets.
 */

export interface LocalModelsStatus {
  reachable: boolean;
  url: string;
  version: string | null;
  models: string[];
  error: string | null;
  /** Missing from desktop builds before the model choice. */
  memory_gb?: number;
  /** colibri, whose models are also in `models` as `colibri/<id>`. Older desktop builds have none. */
  colibri?: { reachable: boolean; url: string; error: string | null };
}

/** The desktop app's bridge to Ollama (`apps/desktop/src/local-models.ts`). */
export interface DesktopLocalModelsBridge {
  status: () => Promise<LocalModelsStatus>;
  /** Ollama's `/v1/systemone`, for decision models such as Laya. */
  decide: (input: unknown) => Promise<unknown>;
  /** Ollama's `/api/chat`, without streaming. */
  chat: (input: unknown) => Promise<unknown>;
  /**
   * Unloads models from Ollama's memory: `except` all loaded models but those,
   * `models` only those. Desktop apps before it have none.
   */
  unload?: (input: { except?: string[]; models?: string[] }) => Promise<unknown>;
}

export interface RudiDesktopBridge {
  voice?: DesktopVoiceBridge;
  /** Practice mode. Desktop apps before practice mode have none. */
  practice?: DesktopPracticeBridge;
  /** Ollama on this computer, for Trial Matcher's local runs. Older desktop apps have none. */
  localModels?: DesktopLocalModelsBridge;
  platform: string;
  version: string;

  // Server config
  getServerUrl: () => Promise<string | null>;
  setServerUrl: (url: string) => Promise<void>;

  // Window management
  newWindow: () => Promise<void>;
  closeWindow: () => Promise<void>;

  // Auto-update
  onUpdateAvailable: (callback: (info: unknown) => void) => void;
  onUpdateDownloaded: (callback: (info: unknown) => void) => void;
  installUpdate: () => void;
}

declare global {
  interface Window {
    rudiDesktop?: RudiDesktopBridge;
  }
}

export function isElectron(): boolean {
  return typeof window !== 'undefined' && !!window.rudiDesktop;
}

export function isBrowser(): boolean {
  return typeof window !== 'undefined' && !window.rudiDesktop;
}
